import React from 'react';
import { Box, SvgIcon, useTheme } from '@mui/material';
import { getBrandName } from '../../../../utils/brandUtils';

interface LogoProps {
  size?: number;
  variant?: 'full' | 'icon' | 'animated';
  showText?: boolean;
}

const LogoIcon: React.FC<{ size: number }> = ({ size }) => {
  const theme = useTheme();
  const logoConfig = theme.logo;

  return (
    <SvgIcon sx={{ width: size, height: size }} viewBox="0 0 64 64">
      <defs>
        <linearGradient id="primaryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: logoConfig.gradients.primary.from, stopOpacity:1}} />
          <stop offset="100%" style={{stopColor: logoConfig.gradients.primary.to, stopOpacity:1}} />
        </linearGradient>
        <linearGradient id="secondaryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: logoConfig.gradients.secondary.from, stopOpacity:1}} />
          <stop offset="100%" style={{stopColor: logoConfig.gradients.secondary.to, stopOpacity:1}} />
        </linearGradient>
      </defs>
      
      {/* Background circle */}
      <circle cx="32" cy="32" r="30" fill="url(#primaryGradient)"/>
      
      {/* Central hub */}
      <circle cx="32" cy="32" r="4" fill="url(#secondaryGradient)"/>
      
      {/* IoT nodes */}
      <circle cx="32" cy="12" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="48" cy="20" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="52" cy="32" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="48" cy="44" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="32" cy="52" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="16" cy="44" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="12" cy="32" r="3" fill={logoConfig.colors.nodes}/>
      <circle cx="16" cy="20" r="3" fill={logoConfig.colors.nodes}/>
      
      {/* Connection lines */}
      <line x1="32" y1="28" x2="32" y2="15" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="36" y1="32" x2="45" y2="23" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="36" y1="32" x2="49" y2="32" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="36" y1="36" x2="45" y2="41" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="32" y1="36" x2="32" y2="49" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="28" y1="36" x2="19" y2="41" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="28" y1="32" x2="15" y2="32" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      <line x1="28" y1="28" x2="19" y2="23" stroke={logoConfig.colors.connections} strokeWidth="1" opacity="0.8"/>
      
      {/* Signal waves */}
      <circle cx="32" cy="32" r="12" fill="none" stroke={logoConfig.colors.signals} strokeWidth="0.5" opacity="0.4"/>
      <circle cx="32" cy="32" r="18" fill="none" stroke={logoConfig.colors.signals} strokeWidth="0.5" opacity="0.2"/>
    </SvgIcon>
  );
};

const Logo: React.FC<LogoProps> = ({ 
  size = 40, 
  variant = 'icon',
  showText = false 
}) => {
  const theme = useTheme();
  const logoConfig = theme.logo;
  const getLogoSrc = () => {
    switch (variant) {
      case 'full':
        return '/logo.svg';
      case 'animated':
        return '/logo-animated.svg';
      case 'icon':
      default:
        return '/logo-icon.svg';
    }
  };

  if (variant === 'icon') {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <LogoIcon size={size} />
        {showText && (
          <Box
            component="span"
            sx={{
              fontFamily: logoConfig.text.fontFamily,
              fontWeight: logoConfig.text.fontWeight,
              fontSize: size * logoConfig.text.sizeFactor,
              color: logoConfig.text.color,
            }}
          >
            {getBrandName()}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        component="img"
        src={getLogoSrc()}
        alt={`${getBrandName()} Logo`}
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
        }}
      />
      {showText && (
        <Box
          component="span"
          sx={{
            fontFamily: logoConfig.text.fontFamily,
            fontWeight: logoConfig.text.fontWeight,
            fontSize: size * (logoConfig.text.sizeFactor * 0.85),
            color: logoConfig.text.color,
          }}
        >
          {getBrandName()}
        </Box>
      )}
    </Box>
  );
};

export default Logo;